import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../assets/css/register.css";
import AdminNavBar from "../components/AdminNavbar";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

axios.defaults.baseURL = API_BASE_URL;

const buildCalendarYears = () => {
  const years = [];
  for (let start = 2023; start <= 2035; start += 1) {
    years.push(`${start}/${start + 1}`);
  }
  return years;
};

const buildEntryYears = () => {
  const years = [];
  for (let year = 2023; year <= 2050; year += 1) {
    years.push(String(year));
  }
  return years;
};

export default function Register() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("student");
  const [matricNo, setMatricNo] = useState("");
  const [username, setUsername] = useState("");
  const [programme, setProgramme] = useState("");
  const [programmes, setProgrammes] = useState([]);
  const [calendarYear, setCalendarYear] = useState("");
  const [studentLevel, setStudentLevel] = useState("");
  const [entryMode, setEntryMode] = useState("");
  const [customEntryMode, setCustomEntryMode] = useState("");
  const [entryYear, setEntryYear] = useState("");
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const navigate = useNavigate();
  const calendarYears = buildCalendarYears();
  const entryYears = buildEntryYears();

  const normalizedRole = String(role || "")
    .trim()
    .toLowerCase();
  const isStudent = normalizedRole === "student";

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/api/programmes");
        setProgrammes(res.data);
      } catch (err) {
        console.error("Error fetching programmes", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isStudent) {
      setCalendarYear("");
      setStudentLevel("");
      setEntryMode("");
      setCustomEntryMode("");
      setEntryYear("");
    }
  }, [isStudent]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    const cleanEmail = String(email || "")
      .trim()
      .toLowerCase();
    const cleanUsername = String(username || "")
      .trim()
      .toLowerCase();
    const cleanMatricNo = String(matricNo || "")
      .trim()
      .toLowerCase();

    try {
      const finalEntryMode =
        isStudent && entryMode === "Others"
          ? customEntryMode.trim()
          : entryMode;

      if (!fullName.trim() || !cleanEmail || !password.trim()) {
        setError("Full name, email and password are required.");
        return;
      }

      if (isStudent) {
        if (!cleanMatricNo) {
          setError("Matric number is required for students.");
          return;
        }

        if (!studentLevel) {
          setError("Student level is required.");
          return;
        }

        if (!entryMode) {
          setError("Entry mode is required.");
          return;
        }

        if (entryMode === "Others" && !customEntryMode.trim()) {
          setError("Please type the entry mode.");
          return;
        }

        if (!calendarYear) {
          setError("Calendar year is required.");
          return;
        }

        if (!entryYear) {
          setError("Entry year is required.");
          return;
        }

        if (cleanEmail === cleanMatricNo) {
          setError("Email and matric number cannot be the same.");
          return;
        }
      } else {
        if (!cleanUsername) {
          setError("Username is required.");
          return;
        }

        if (cleanEmail === cleanUsername) {
          setError("Email and username cannot be the same.");
          return;
        }
      }

      await axios.post("/api/register", {
        fullName: fullName.trim(),
        email: email.trim(),
        password,
        role: normalizedRole,
        programme: normalizedRole === "admin" ? null : programme,
        matricNo: isStudent ? matricNo.trim() : null,
        username: !isStudent ? username.trim() : null,
        CalenderYear: isStudent ? calendarYear : null,
        StudentLevel: isStudent ? studentLevel : null,
        entryYear: isStudent ? entryYear : null,
        entryMode: isStudent ? finalEntryMode : null,
      });

      setShowSuccess(true);
    } catch (err) {
      setError(
        err.response?.status === 409
          ? err.response.data.msg
          : err.response?.data?.msg || "Registration failed. Please try again.",
      );
    }
  };

  const handleSuccessOk = () => {
    setShowSuccess(false);
    window.location.reload();
  };

  return (
    <div style={{ marginTop: "-80px" }}>
      <AdminNavBar />
      <div className="register-page">
        <div className="register-container">
          <h2>Register</h2>

          {error && <p className="error-message">{error}</p>}

          <form onSubmit={handleRegister}>
            <input
              type="text"
              placeholder="Full Name"
              className="register-input"
              onChange={(e) => setFullName(e.target.value)}
              required
            />

            <input
              type="email"
              placeholder="Email"
              className="register-input"
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <input
              type="password"
              placeholder="Password"
              className="register-input"
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <select
              className="register-input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="student">Student</option>
              <option value="staff">Staff</option>
              <option value="hod">HOD</option>
              <option value="admin">Admin</option>
            </select>

            {isStudent && (
              <>
                <input
                  type="text"
                  placeholder="Matric No"
                  className="register-input"
                  onChange={(e) => setMatricNo(e.target.value)}
                  required
                />

                <select
                  className="register-input"
                  value={studentLevel}
                  onChange={(e) => setStudentLevel(e.target.value)}
                  required
                >
                  <option value="">Select Student Level</option>
                  <option value="ND1">ND1</option>
                  <option value="ND2">ND2</option>
                  <option value="HND1">HND1</option>
                  <option value="HND2">HND2</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Postgraduate">Postgraduate</option>
                  <option value="P1">P1</option>
                  <option value="P2">P2</option>
                </select>

                <select
                  className="register-input"
                  value={entryMode}
                  onChange={(e) => setEntryMode(e.target.value)}
                  required
                >
                  <option value="">Select Entry Mode</option>
                  <option value="Jamb">Jamb</option>
                  <option value="Waec">Waec</option>
                  <option value="GCE">GCE</option>
                  <option value="Predegree">Predegree</option>
                  <option value="Postgraduate">Postgraduate</option>
                  <option value="Others">Others</option>
                </select>

                {entryMode === "Others" && (
                  <input
                    type="text"
                    placeholder="Type Entry Mode"
                    className="register-input"
                    value={customEntryMode}
                    onChange={(e) => setCustomEntryMode(e.target.value)}
                    required
                  />
                )}

                <select
                  className="register-input"
                  value={entryYear}
                  onChange={(e) => setEntryYear(e.target.value)}
                  required
                >
                  <option value="">Select Entry Year</option>
                  {entryYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>

                <select
                  className="register-input"
                  value={calendarYear}
                  onChange={(e) => setCalendarYear(e.target.value)}
                  required
                >
                  <option value="">Select Calendar Year</option>
                  {calendarYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </>
            )}

            {!isStudent && (
              <input
                type="text"
                placeholder="Username"
                className="register-input"
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            )}

            {normalizedRole !== "admin" && (
              <select
                className="register-input"
                value={programme}
                onChange={(e) => setProgramme(e.target.value)}
                required
              >
                <option value="">Select Programme</option>
                {programmes.map((prog, idx) => (
                  <option key={idx} value={prog.Programme}>
                    {prog.Programme}
                  </option>
                ))}
              </select>
            )}

            <button type="submit" className="register-button">
              Register
            </button>
          </form>
        </div>

        {showSuccess && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Success!</h3>
              <p>Your account has been created.</p>
              <button onClick={handleSuccessOk}>OK</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
